/**
 * FPS + frame-time HUD. Toggle with `?` (Shift+/).
 * Exposes `window.__perf.snapshot()` for scripted capture across runs.
 */

declare global {
  interface Window {
    __perf?: {
      snapshot(): PerfSnapshot;
      reset(): void;
    };
  }
  interface Performance {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number };
  }
}

export interface PerfSnapshot {
  fps: number;
  avgFrameMs: number;
  worstFrameMs: number;
  framesSampled: number;
  histogram: { '<8ms': number; '8-16ms': number; '16-33ms': number; '33-50ms': number; '>50ms': number };
  jsHeapMB: number | null;
  timeToInteractiveMs: number | null;
}

const BUCKET_LABELS = ['<8ms', '8-16ms', '16-33ms', '33-50ms', '>50ms'] as const;

export class PerfHud {
  private readonly el: HTMLElement;
  private visible = false;

  private last = performance.now();
  private samples: number[] = [];
  private worst5sStart = performance.now();
  private worst5s = 0;
  private tti: number | null = null;
  private rafId = 0;

  constructor() {
    const el = document.getElementById('perf-hud');
    if (!el) throw new Error('PerfHud: #perf-hud missing');
    this.el = el;

    window.__perf = {
      snapshot: () => this.snapshot(),
      reset: () => this.reset(),
    };

    window.addEventListener('keydown', (e) => {
      if (e.key === '?' || (e.shiftKey && e.key === '/')) this.toggle();
    });
  }

  markInteractive(): void {
    if (this.tti !== null) return;
    this.tti = performance.now();
  }

  start(): void {
    const loop = (now: number): void => {
      const dt = now - this.last;
      this.last = now;
      this.samples.push(dt);
      if (this.samples.length > 600) this.samples.shift();
      if (dt > this.worst5s) this.worst5s = dt;
      if (now - this.worst5sStart > 5000) {
        this.worst5sStart = now;
        this.worst5s = 0;
      }
      if (this.visible) this.render();
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
  }

  toggle(): void {
    this.visible = !this.visible;
    this.el.classList.toggle('visible', this.visible);
    if (this.visible) this.render();
  }

  reset(): void {
    this.samples = [];
    this.worst5s = 0;
    this.worst5sStart = performance.now();
  }

  snapshot(): PerfSnapshot {
    const s = this.samples;
    const n = s.length || 1;
    const sum = s.reduce((a, b) => a + b, 0);
    const avg = sum / n;
    const hist = [0, 0, 0, 0, 0];
    let worst = 0;
    for (const d of s) {
      if (d > worst) worst = d;
      if (d < 8) hist[0]++;
      else if (d < 16) hist[1]++;
      else if (d < 33) hist[2]++;
      else if (d < 50) hist[3]++;
      else hist[4]++;
    }
    const mem = performance.memory;
    return {
      fps: avg > 0 ? Math.round(1000 / avg) : 0,
      avgFrameMs: round1(avg),
      worstFrameMs: round1(worst),
      framesSampled: n,
      histogram: {
        '<8ms': hist[0],
        '8-16ms': hist[1],
        '16-33ms': hist[2],
        '33-50ms': hist[3],
        '>50ms': hist[4],
      },
      jsHeapMB: mem ? round1(mem.usedJSHeapSize / 1048576) : null,
      timeToInteractiveMs: this.tti != null ? Math.round(this.tti) : null,
    };
  }

  private render(): void {
    const snap = this.snapshot();
    const histLines = BUCKET_LABELS.map((label, i) => {
      const counts = Object.values(snap.histogram)[i];
      const bar = '█'.repeat(Math.min(20, Math.floor((counts / Math.max(1, snap.framesSampled)) * 20)));
      return `  ${label.padEnd(7)} ${bar} ${counts}`;
    }).join('\n');

    this.el.textContent = [
      `FPS  ${snap.fps.toString().padStart(3)}   avg ${snap.avgFrameMs}ms`,
      `worst (5s) ${round1(this.worst5s)}ms   ever ${snap.worstFrameMs}ms`,
      `heap ${snap.jsHeapMB ?? '—'} MB   tti ${snap.timeToInteractiveMs ?? '—'} ms`,
      `frames sampled: ${snap.framesSampled}`,
      histLines,
      `(press ? to hide)`,
    ].join('\n');
  }
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
